import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { createCycleCount, listCycleCounts } from '@/lib/api/inventory-counts.js';
import { formatCurrency, formatDate, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

export function CycleCountListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const listQuery = useQuery({ queryKey: ['cycle-counts'], queryFn: listCycleCounts });

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#164477]"
        >
          <Plus className="h-4 w-4" />
          New count
        </button>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-5 py-3">#</th>
              <th className="px-5 py-3">Type</th>
              <th className="px-5 py-3">Started by</th>
              <th className="px-5 py-3">Started</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Variance $</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {listQuery.data?.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="px-5 py-4 font-mono text-xs text-slate-600">{c.id.slice(0, 8)}…</td>
                <td className="px-5 py-4">{prettyLabel(c.countType)}</td>
                <td className="px-5 py-4">{c.startedByName ?? '—'}</td>
                <td className="px-5 py-4">{formatDate(c.startedAt)}</td>
                <td className="px-5 py-4">{prettyLabel(c.status)}</td>
                <td className="px-5 py-4">{formatCurrency(c.varianceValue)}</td>
                <td className="px-5 py-4 text-right">
                  <Link to={`/inventory/counts/${c.id}`} className="text-sm font-semibold text-primary">
                    Open
                  </Link>
                </td>
              </tr>
            ))}
            {listQuery.isLoading ? (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : null}
            {!listQuery.isLoading && listQuery.data?.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-slate-500">
                  No cycle counts yet
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {modalOpen ? (
        <NewCountModal
          onClose={() => setModalOpen(false)}
          onCreated={(cid) => {
            queryClient.invalidateQueries({ queryKey: ['cycle-counts'] });
            setModalOpen(false);
            navigate(`/inventory/counts/${cid}`);
          }}
        />
      ) : null}
    </div>
  );
}

function NewCountModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [countType, setCountType] = useState<'full' | 'category' | 'location'>('full');
  const [filterCategory, setFilterCategory] = useState('repair_part');
  const [filterBin, setFilterBin] = useState('');
  const [blind, setBlind] = useState(true);
  const [notes, setNotes] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      createCycleCount({
        countType,
        filterCategory:
          countType === 'category'
            ? (filterCategory as 'repair_part' | 'retail_accessory' | 'trade_in' | 'refurbished')
            : undefined,
        filterBinLocation: countType === 'location' ? filterBin : undefined,
        blindCount: blind,
        notes: notes || undefined,
      }),
    onSuccess: (d) => onCreated(d.id),
    onError: (e) => toastError(parseApiError(e).message),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mut.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="text-lg font-bold text-slate-950">New cycle count</h3>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Count type</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
              value={countType}
              onChange={(e) => setCountType(e.target.value as 'full' | 'category' | 'location')}
            >
              <option value="full">Full store</option>
              <option value="category">Category</option>
              <option value="location">Location</option>
            </select>
          </label>
          {countType === 'category' ? (
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Category</span>
              <select
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <option value="repair_part">Repair part</option>
                <option value="retail_accessory">Retail accessory</option>
                <option value="trade_in">Trade-in</option>
                <option value="refurbished">Refurbished</option>
              </select>
            </label>
          ) : null}
          {countType === 'location' ? (
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Bin location</span>
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={filterBin}
                onChange={(e) => setFilterBin(e.target.value)}
                required
              />
            </label>
          ) : null}
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={blind} onChange={(e) => setBlind(e.target.checked)} className="h-4 w-4" />
            Blind count (hide expected in UI when active)
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-700">Notes</span>
            <textarea className="mt-1 w-full rounded-xl border px-3 py-2" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="rounded-xl border px-4 py-2 text-sm font-semibold" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={mut.isPending}
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              Start count
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
